// ---------------------------------------------------------------------------
// DSS-17: 16 blend modes dropdown — preview swatches, hover preview, CSS output
// ---------------------------------------------------------------------------
import { useState, useCallback, useRef, useEffect } from "react";
import { Blend } from "lucide-react";

// ── Constants ──
const BLEND_MODES = [
  "normal", "multiply", "screen", "overlay",
  "darken", "lighten", "color-dodge", "color-burn",
  "hard-light", "soft-light", "difference", "exclusion",
  "hue", "saturation", "color", "luminosity",
] as const;

type BlendMode = (typeof BLEND_MODES)[number];

const SWATCH_COLOR_A = "#3b82f6";
const SWATCH_COLOR_B = "#f97316";
const SWATCH_SIZE_SM = 16;
const SWATCH_SIZE_LG = 40;

// ── CSS generation ──
export function generateBlendModeCss(mode: string): string {
  return `mix-blend-mode: ${mode};`;
}

// ── Props ──
interface BlendModePickerProps {
  readonly value: string;
  readonly onChange: (mode: string) => void;
}

// ── Component ──
export default function BlendModePicker({ value, onChange }: BlendModePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredMode, setHoveredMode] = useState<BlendMode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;

    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setIsOpen(false);
    }

    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  const handleSelect = useCallback(
    (mode: BlendMode) => {
      onChange(mode);
      setIsOpen(false);
    },
    [onChange],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Blend className="h-3.5 w-3.5" />
        Blend Mode
      </div>

      {/* Dropdown trigger */}
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={[
            "flex w-full items-center gap-2 rounded-lg border px-3 py-2",
            isOpen
              ? "border-[var(--color-primary)] bg-[var(--color-card)]"
              : "border-[var(--color-border)] bg-[var(--color-card)] hover:border-[var(--color-primary)]/50",
          ].join(" ")}
        >
          <BlendSwatch mode={value} size={SWATCH_SIZE_SM} />
          <span className="flex-1 text-left text-xs text-[var(--color-foreground)]">
            {value}
          </span>
          <svg
            className={[
              "h-3 w-3 text-[var(--color-muted-foreground)] transition-transform",
              isOpen ? "rotate-180" : "",
            ].join(" ")}
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M3 5l3 3 3-3" />
          </svg>
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] shadow-lg">
            {/* Hover preview */}
            {hoveredMode && (
              <div className="sticky top-0 flex items-center justify-center border-b border-[var(--color-border)] bg-[var(--color-muted)] p-2">
                <BlendSwatch mode={hoveredMode} size={SWATCH_SIZE_LG} />
                <span className="ml-2 text-[10px] font-medium text-[var(--color-foreground)]">
                  {hoveredMode}
                </span>
              </div>
            )}

            {BLEND_MODES.map((mode) => {
              const isActive = value === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => handleSelect(mode)}
                  onMouseEnter={() => setHoveredMode(mode)}
                  onMouseLeave={() => setHoveredMode(null)}
                  className={[
                    "flex w-full items-center gap-2 px-3 py-1.5",
                    isActive
                      ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                      : "text-[var(--color-foreground)] hover:bg-[var(--color-muted)]",
                  ].join(" ")}
                >
                  <BlendSwatch mode={mode} size={SWATCH_SIZE_SM} />
                  <span className="text-[11px]">{mode}</span>
                  {isActive && (
                    <svg className="ml-auto h-3 w-3" viewBox="0 0 12 12" fill="currentColor">
                      <path d="M10 3L5 9 2 6" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* CSS output */}
      <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
        <code className="text-[10px] text-[var(--color-muted-foreground)]">
          {generateBlendModeCss(value)}
        </code>
      </div>
    </div>
  );
}

// ── Blend swatch — two overlapping squares with blend mode applied ──
interface BlendSwatchProps {
  readonly mode: string;
  readonly size: number;
}

function BlendSwatch({ mode, size }: BlendSwatchProps) {
  const half = Math.round(size * 0.6);
  const offset = Math.round(size * 0.4);

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <div
        className="absolute rounded-sm"
        style={{
          width: half,
          height: half,
          top: 0,
          left: 0,
          backgroundColor: SWATCH_COLOR_A,
        }}
      />
      <div
        className="absolute rounded-sm"
        style={{
          width: half,
          height: half,
          top: offset,
          left: offset,
          backgroundColor: SWATCH_COLOR_B,
          mixBlendMode: mode as React.CSSProperties["mixBlendMode"],
        }}
      />
    </div>
  );
}
